import classNames from "classnames";
import { ChangeEvent, CSSProperties, InputHTMLAttributes, useId } from "react";
import { Control, Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import styles from "./input.module.scss";

type Props = {
  name: string;
  postNote?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  clearable?: boolean;
  label?: string;
  mask?: string | (string | RegExp)[];
} & InputHTMLAttributes<HTMLInputElement>;

interface ExtendedCSS extends CSSProperties {
  "--after-right": string;
}

export const Input = ({
  label,
  postNote,
  type = "text",
  className,
  control,
  name,
  clearable = false,
  ...inputProps
}: Props) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur, value },
        formState: { errors },
      }) => {
        const fieldArray = name.match(/(\w+)\.(\d+)/);
        const errorField =
          errors[name] ||
          (fieldArray &&
            errors &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (errors[fieldArray[1]] as any)?.[fieldArray[2]]);

        const error = errorField?.message;

        return (
          <BaseInput
            className={className}
            {...inputProps}
            postNote={postNote}
            label={label}
            error={error}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            type={type}
            clearable={clearable}
          />
        );
      }}
    />
  );
};

export const BaseInput = ({
  className,
  postNote,
  label,
  error,
  value,
  onChange,
  onBlur,
  type,
  icon,
  onClick,
  disabled,
  mask,
  clearable = false,
  ...inputProps
}: Omit<Props, "control" | "name"> & {
  error?: string;
}) => {
  const id = useId();

  const extendedCss: ExtendedCSS = {
    "--after-right": type === "number" ? "5px" : "0px",
  };

  const inputClassName = classNames(
    styles.input,
    !!error && styles.inputError,
    !!icon && styles.inputWithIcon,
    !!onClick && styles.inputDisabled
  );

  const commonInputProps = {
    className: inputClassName,
    disabled: disabled || !!onClick,
    value,
    onChange,
    onBlur,
    id,
    type,
    ...inputProps,
  };

  return (
    <div
      className={classNames(styles.inputGroup, className)}
      data-meta={postNote}
      style={extendedCss}
    >
      {label && <label htmlFor={id}>{label}</label>}
      <div
        className={classNames(
          styles.inputWrapper,
          !!onClick && styles.inputWrapperClickable
        )}
        onClick={onClick}
      >
        {mask ? (
          <InputMask mask={mask} {...commonInputProps}>
            {(inputProps) => <input {...inputProps} />}
          </InputMask>
        ) : (
          <input {...commonInputProps} step={type == "number" ? "any" : undefined} />
        )}
        {clearable && !disabled && (
          <div
            className={classNames(
              styles.clearIcon,
              value != null && value !== ""
                ? styles.clearIconVisible
                : styles.clearIconHidden
            )}
            onClick={(event) => {
              event.stopPropagation();
              onChange &&
                onChange({
                  target: { value: "" },
                } as ChangeEvent<HTMLInputElement>);
            }}
          >
            ×
          </div>
        )}
        {icon && <div className={styles.icon}>{icon}</div>}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};
